import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface TaskListMember {
  id: string;
  profile_id: string;
  task_list_id: string;
  profiles?: {
    id: string;
    first_name: string;
    last_name: string;
    avatar_url?: string;
  };
}

export function useTaskListMembers(listId?: string) {
  return useQuery({
    queryKey: ['task-list-members', listId],
    queryFn: async (): Promise<TaskListMember[]> => {
      if (!listId) return [];

      // First get task list members
      const { data: membersData, error: membersError } = await supabase
        .from('task_list_members')
        .select('id, profile_id, task_list_id')
        .eq('task_list_id', listId);

      if (membersError) throw membersError;
      if (!membersData || membersData.length === 0) return [];

      // Then get profiles for these members
      const profileIds = membersData.map(m => m.profile_id);
      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, avatar_url')
        .in('id', profileIds);

      if (profilesError) throw profilesError;

      // Combine the data
      return membersData.map(member => ({
        ...member,
        profiles: (profilesData || []).find(p => p.id === member.profile_id)
      }));
    },
    enabled: !!listId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}